import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchProjectActivity,
  fetchProjectChatter,
  type ProjectActivityEntry,
  type ProjectChatterEntry,
} from "@/api/activityLog";

interface DashboardActivityWidgetProps {
  projectId: number;
  /** How many recent entries to show. Expected mount-stable (DashboardTab passes
   *  the default); a change blanks the list + reloads, same as a project switch. */
  limit?: number;
  /** Rendered inside a DashboardSection coloured shell — drop our own outer card
   *  chrome and the duplicated "Recente activiteit" title (the shell owns both). */
  embedded?: boolean;
  /** Report the chatter feed's entry count up so the collapsed section header can
   *  show "N acties" without a duplicate fetch. Null until the first load resolves. */
  onCountChange?: (count: number | null) => void;
  /** Whether the caller may read the Odoo-chatter feed (`project:chatter:read`,
   *  admin-only/GDPR-gated). When false the chatter fetch is SKIPPED entirely (it
   *  would 403) and the portal activity-log becomes the primary feed. Default true
   *  preserves the admin experience. */
  chatterEnabled?: boolean;
}

/** Poll cadence — scoped polling, not SSE (the /admin/activity stream broadcasts
 *  every project's events unfiltered, so it can't be reused here). Matches the
 *  60 s server-side chatter cache TTL. */
const POLL_MS = 60_000;

/** Initial number of chatter rows shown before "+ 5 meer" pagination kicks in. */
const INITIAL_VISIBLE = 8;

/** How many extra rows each "+ N meer" click reveals. */
const PAGE_STEP = 5;

/**
 * Leading line icon per row — heroicons-outline style (monochrome, currentColor,
 * round caps) to match the portal's hand-rolled SVG idiom. Mapped by ACTION
 * first, then kind. Restrained palette: comms = blue, positive milestones =
 * green, planning = amber, the rest muted grey. No pastel chips / emoji.
 */
interface IconSpec {
  path: string;
  color: string;
}

const ICON_PATHS = {
  calendar: "M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5",
  user: "M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z",
  checkCircle: "M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
  pencil: "M16.862 4.487l1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125",
  badge: "M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z",
  tag: "M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z",
  star: "M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z",
  plusCircle: "M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
  adjustments: "M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75",
  envelope: "M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75",
  chat: "M2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 0 1 1.037-.443 48.282 48.282 0 0 0 5.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z",
  dot: "M12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9Z",
  banknotes: "M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z",
} as const;

const VORDERING_ICON: IconSpec = { path: ICON_PATHS.banknotes, color: "text-td-green" };
const INVOICE_ICON: IconSpec = { path: ICON_PATHS.banknotes, color: "text-td-purple" };

const ACTION_ICON: Record<string, IconSpec> = {
  "Planning bijgewerkt": { path: ICON_PATHS.calendar, color: "text-amber-600" },
  "Installateur toegewezen": { path: ICON_PATHS.user, color: "text-td-green" },
  "Taak afgewerkt": { path: ICON_PATHS.checkCircle, color: "text-td-green" },
  "Tekenbureau-opmerking": { path: ICON_PATHS.pencil, color: "text-td-purple" },
  "Verkooporder bevestigd": { path: ICON_PATHS.badge, color: "text-td-green" },
  "Verkooporder-status": { path: ICON_PATHS.badge, color: "text-td-green" },
  "Projectfase gewijzigd": { path: ICON_PATHS.tag, color: "text-td-navy" },
  "Opportunity gewonnen": { path: ICON_PATHS.star, color: "text-amber-500" },
  "Opportunity aangemaakt": { path: ICON_PATHS.plusCircle, color: "text-td-muted" },
};

const EMAIL_ICON: IconSpec = { path: ICON_PATHS.envelope, color: "text-td-blue" };
const COMMENT_ICON: IconSpec = { path: ICON_PATHS.chat, color: "text-td-blue" };
const FALLBACK_ICON: IconSpec = { path: ICON_PATHS.dot, color: "text-td-muted" };

/** Pick the leading icon: action wins, then kind (email/comment), else fallback.
 *  Vordering/Factuur actions are dynamic ("Vordering 1 ingediend") → matched by prefix. */
function chatterIcon(entry: ProjectChatterEntry): IconSpec {
  if (entry.kind === "email") return EMAIL_ICON;
  if (entry.kind === "comment") return COMMENT_ICON;
  if (entry.action.startsWith("Vordering")) return VORDERING_ICON;
  if (entry.action.startsWith("Factuur")) return INVOICE_ICON;
  return ACTION_ICON[entry.action] ?? FALLBACK_ICON;
}

/** Strip a single leading `→ ` (installateur detail is `→ A&A company`). */
function stripLeadingArrow(detail: string): string {
  return detail.replace(/^\s*→\s*/, "");
}

/**
 * Compact milestone headline for non-comment / non-email rows.
 * Planning, installateur and taak-afgewerkt lead with the fase (`taskName`) in
 * bold; everything else leads with the Dutch action. Detail is muted. Every
 * branch renders the dynamic fields as React TEXT only (security contract).
 */
function ChatterMilestoneHeadline({ entry }: { entry: ProjectChatterEntry }) {
  const { action, taskName, detail } = entry;

  // Planning: `Fase 1= +00 · ingepland 21–26 mei · 4 taken`.
  if (action === "Planning bijgewerkt" && taskName) {
    return (
      <span className="leading-snug break-words text-td-navy">
        <span className="font-semibold">{taskName}</span>
        <span className="text-td-muted">
          {" "}
          · ingepland{detail ? ` ${detail}` : ""}
        </span>
      </span>
    );
  }

  // Installateur: `Fase 1= +00 · A&A company` (leading "→ " stripped).
  if (action === "Installateur toegewezen" && taskName) {
    return (
      <span className="leading-snug break-words text-td-navy">
        <span className="font-semibold">{taskName}</span>
        {detail && (
          <span className="text-td-muted"> · {stripLeadingArrow(detail)}</span>
        )}
      </span>
    );
  }

  // Taak afgewerkt: `Fase 1= +00 afgewerkt` — plus `· N taken` when several
  // sub-tasks of the fase were completed together (the status old→new is dropped;
  // only a grouped count, which matches /^\d+ taken/, is shown).
  if (action === "Taak afgewerkt" && taskName) {
    const isCount = !!detail && /^\d+\s+taken$/.test(detail);
    return (
      <span className="leading-snug break-words text-td-navy">
        <span className="font-semibold">{taskName}</span>
        <span className="text-td-muted"> afgewerkt{isCount ? ` · ${detail}` : ""}</span>
      </span>
    );
  }

  // Generic milestone: action bold + muted detail.
  return (
    <span className="leading-snug break-words text-td-navy">
      <span className="font-semibold">{action}</span>
      {detail && <span className="text-td-muted"> — {detail}</span>}
    </span>
  );
}

/** Dutch relative-time label. `now` is injectable for testing. */
export function formatRelativeTime(
  ts: string,
  now: number = Date.now(),
): string {
  const then = new Date(ts).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 0) return "zojuist";
  if (diffSec < 60) return "zojuist";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min geleden`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour} u geleden`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay} d geleden`;
  return new Date(ts).toLocaleDateString("nl-BE");
}

/**
 * Project-scoped activity widget (Fase 7 → chatter-feed Phase 5, PL-focused
 * rework). The PRIMARY feed is the aggregated, server-cleaned Odoo-chatter
 * stream; the portal activity-log renders as a thin labelled tail below a
 * divider. Both streams are fired in parallel by one `load()`, share the
 * `projectIdRef` stale-guard, skip while the tab is hidden, re-fire on
 * visibilitychange, and keep the last good list on a failed poll. Poll cadence
 * is POLL_MS.
 *
 * Rendering (PL-focused): each row is ONE tight line led by a round emoji icon
 * (the old text source chip is gone). Communication reads message-first
 * (comments: author bold + message; emails: author/subject + snippet + inline
 * expand). Milestones are compact: planning shows the fase name + date range,
 * installateur/taak-afgewerkt show the fase name (grouped rows with a per-item
 * expander when a fase bundles several tasks).
 *
 * Interactive layer (Phase 5): incremental "+ 5 meer" / "minder" pagination
 * (client-side, never re-fetch), a header "ververs" button, and inline plain-text
 * email expand with a direction badge.
 *
 * SECURITY: every text field (action/detail/snippet/userName/fullText/taskName)
 * is rendered as React text only. The backend guarantees plain text; this widget
 * must NEVER reintroduce an HTML sink (no dangerouslySetInnerHTML, no SafeHtml).
 */
export function DashboardActivityWidget({
  projectId,
  limit = 5,
  embedded = false,
  onCountChange,
  chatterEnabled = true,
}: DashboardActivityWidgetProps) {
  const [activity, setActivity] = useState<ProjectActivityEntry[]>([]);
  const [chatter, setChatter] = useState<ProjectChatterEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // Interactive view state — deliberately OUTSIDE the reset effect's deps:
  // paging more rows in or expanding an email never reloads the feed, and the
  // choices persist across polls + a manual ververs. (They DO reset on a project
  // switch — see the reset effect below.)
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Latest projectId so an in-flight poll fired for a previous project can be dropped.
  const projectIdRef = useRef(projectId);

  // Shared fetch body for the polling effect AND the ververs button. `force`
  // busts the per-project server cache (the "ververs" path). Stable identity so
  // it is not an effect dependency.
  const fetchBoth = useCallback(
    (force: boolean) => {
      // Skip polling while the tab is hidden — no point hammering the API. (A
      // manual ververs always passes force=true and is allowed through.)
      if (!force && typeof document !== "undefined" && document.hidden) return;

      // Chatter is the PRIMARY feed and owns the loading/error states — but ONLY
      // when the caller may read it (project:chatter:read). Without that perm the
      // endpoint 403s, so skip it and let the activity-log tail below become the
      // primary feed (owning loading/error instead).
      if (chatterEnabled) {
        fetchProjectChatter(projectId, force ? { refresh: true } : undefined)
          .then((res) => {
            if (projectIdRef.current !== projectId) return;
            setChatter(res.entries);
            setError(null);
            setLoading(false);
            if (force) setRefreshing(false);
          })
          .catch((err: unknown) => {
            if (projectIdRef.current !== projectId) return;
            // Last-good-on-error: keep whatever chatter we already have.
            setError(
              err instanceof Error ? err.message : "Activiteit laden mislukt",
            );
            setLoading(false);
            if (force) setRefreshing(false);
          });
      }

      // Portal activity-log: a thin labelled tail when chatter is the primary feed
      // (silent failure), OR the primary feed itself when chatter is disabled (then
      // it owns loading/error).
      fetchProjectActivity(projectId, limit)
        .then((res) => {
          if (projectIdRef.current !== projectId) return;
          setActivity(res.entries);
          if (!chatterEnabled) {
            setError(null);
            setLoading(false);
            if (force) setRefreshing(false);
          }
        })
        .catch((err: unknown) => {
          if (projectIdRef.current !== projectId) return;
          if (!chatterEnabled) {
            setError(err instanceof Error ? err.message : "Activiteit laden mislukt");
            setLoading(false);
            if (force) setRefreshing(false);
          }
          // When chatter is primary: keep the last good tail; never surface its error.
        });
    },
    [projectId, limit, chatterEnabled],
  );

  // Manual "ververs" — force-refresh both streams, busting the server cache.
  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchBoth(true);
  }, [fetchBoth]);

  useEffect(() => {
    projectIdRef.current = projectId;
    // Reset the feed AND the interactive view for the new project so neither a
    // stale list nor a stale chip/expand selection survives a switch.
    setActivity([]);
    setChatter([]);
    setLoading(true);
    setError(null);
    setRefreshing(false);
    setVisibleCount(INITIAL_VISIBLE);
    setExpanded(new Set());

    fetchBoth(false);
    const id = setInterval(() => fetchBoth(false), POLL_MS);
    // Mounting while the tab is hidden skips the first load; fire it as soon as
    // the tab becomes visible instead of waiting up to a full poll interval.
    const onVisible = () => {
      if (typeof document !== "undefined" && !document.hidden) fetchBoth(false);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // visibleCount/expanded are intentionally NOT deps — see state comment.
  }, [projectId, fetchBoth]);

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const hasChatter = chatter.length > 0;
  const hasActivity = activity.length > 0;
  // The primary feed is chatter when permitted, else the activity-log tail.
  const primaryLen = chatterEnabled ? chatter.length : activity.length;
  const hasPrimary = chatterEnabled ? hasChatter : hasActivity;

  // Report the primary feed size up for the collapsed section header ("N acties").
  // Null while the first load is still pending (empty feed not yet meaningful).
  useEffect(() => {
    onCountChange?.(loading && !hasPrimary ? null : primaryLen);
  }, [loading, hasPrimary, primaryLen, onCountChange]);

  // Incremental pagination: show the first `visibleCount` rows, reveal +5 at a time.
  const visible = chatter.slice(0, visibleCount);
  const remaining = chatter.length - visibleCount;
  const canShowMore = remaining > 0;
  const canShowLess = visibleCount > INITIAL_VISIBLE;

  const Wrapper = embedded ? 'div' : 'section';
  return (
    <Wrapper
      aria-label="Projectactiviteit"
      className={embedded ? '' : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm'}
    >
      {/* When embedded, the DashboardSection shell owns the title — keep only the
          ververs control, right-aligned. */}
      <div className={`flex items-center gap-3 ${embedded ? 'justify-end' : 'justify-between'}`}>
        {!embedded && (
          <h3 className="text-base font-extrabold text-td-navy">
            Recente activiteit
          </h3>
        )}
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="flex items-center gap-1 text-xs text-td-muted hover:text-td-blue disabled:opacity-50"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            aria-hidden="true"
            className={refreshing ? "animate-spin" : undefined}
          >
            <path d="M21 12a9 9 0 1 1-2.6-6.4M21 4v5h-5" />
          </svg>
          ververs
        </button>
      </div>

      {/* Gates key off the PRIMARY feed (hasPrimary === hasChatter when chatter is
          enabled, else the activity tail). This preserves last-good-on-error for the
          activity-primary (projectleider) path — a transient poll failure must not
          replace an already-loaded list with an error. */}
      {loading && !hasPrimary ? (
        <p className="mt-3 text-sm text-td-muted">Activiteit laden…</p>
      ) : error && !hasPrimary ? (
        <p className="mt-3 text-sm text-td-magenta">
          Activiteit laden mislukt: {error}
        </p>
      ) : !hasPrimary && !hasActivity ? (
        <p className="mt-3 text-sm text-td-muted">
          Nog geen activiteit voor dit project.
        </p>
      ) : (
        <>
          {hasChatter && (
            <>
              <ul className="mt-3 space-y-1.5">
                {visible.map((entry) => {
                  const icon = chatterIcon(entry);
                  const isEmail = entry.kind === "email";
                  const isComment = entry.kind === "comment";
                  const isOpen = expanded.has(entry.id);
                  const rowText = "text-sm";
                  const hasItems =
                    !isEmail && !isComment && !!entry.items && entry.items.length > 0;
                  // Headline + author for a milestone row (computed once so the whole
                  // headline can be wrapped in one click target when it has an items
                  // list — the tiny arrow alone is a poor tap target).
                  const milestoneHeadline = <ChatterMilestoneHeadline entry={entry} />;
                  const milestoneAuthor = entry.userName ? (
                    <span className="ml-1 align-middle text-[11px] text-td-muted">
                      · {entry.userName}
                    </span>
                  ) : null;
                  return (
                    <li
                      key={entry.id}
                      className={`flex items-start gap-2.5 rounded-lg px-1.5 py-1 ${rowText}`}
                    >
                      <svg
                        className={`mt-0.5 h-[18px] w-[18px] flex-none ${icon.color}`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d={icon.path} />
                      </svg>

                      <span className="min-w-0 flex-1">
                        {/* --- communication: COMMENT (message-first) --- */}
                        {isComment ? (
                          <span className="leading-snug break-words text-td-navy">
                            {entry.userName && (
                              <span className="font-semibold">
                                {entry.userName}
                              </span>
                            )}
                            {entry.detail && (
                              <>
                                {entry.userName ? " — " : ""}
                                {entry.detail}
                              </>
                            )}
                          </span>
                        ) : isEmail ? (
                          /* --- communication: EMAIL (subject/sender + snippet + expand) --- */
                          <span className="leading-snug break-words text-td-navy">
                            <span className="font-semibold">
                              {entry.userName || "Klant"}
                            </span>
                            {entry.direction && (
                              <span
                                className={`ml-1.5 inline-block rounded-full px-1.5 py-0.5 align-middle text-[10px] font-bold ${
                                  entry.direction === "inkomend"
                                    ? "bg-gray-100 text-td-magenta"
                                    : "bg-gray-100 text-td-blue"
                                }`}
                              >
                                {entry.direction === "inkomend"
                                  ? "klant → Thermoduct"
                                  : "Thermoduct → klant"}
                              </span>
                            )}
                            <span className="text-td-navy"> — {entry.action}</span>
                            {entry.snippet && (
                              <span className="break-words text-td-muted">
                                {" "}
                                — {entry.snippet}
                              </span>
                            )}
                            {entry.fullText && (
                              <button
                                type="button"
                                aria-expanded={isOpen}
                                onClick={() => toggleExpanded(entry.id)}
                                className="ml-1.5 align-middle text-xs font-semibold text-td-blue hover:underline"
                              >
                                {isOpen ? "minder" : "meer"}
                              </button>
                            )}
                            {isOpen && entry.fullText && (
                              <span className="mt-1 block whitespace-pre-wrap break-words text-xs text-td-text">
                                {entry.fullText}
                              </span>
                            )}
                          </span>
                        ) : hasItems ? (
                          /* --- grouped milestone WITH a list → whole headline is the click target --- */
                          <button
                            type="button"
                            aria-expanded={isOpen}
                            onClick={() => toggleExpanded(entry.id)}
                            className="text-left hover:text-td-navy"
                          >
                            {milestoneHeadline}
                            {milestoneAuthor}
                            <span className="ml-1 align-middle text-xs font-semibold text-td-blue">
                              {isOpen ? "▴" : "▾"}
                            </span>
                          </button>
                        ) : (
                          /* --- milestone without a list --- */
                          <>
                            {milestoneHeadline}
                            {milestoneAuthor}
                          </>
                        )}

                        {/* Expanded list of the underlying tasks/changes. */}
                        {hasItems && isOpen && (
                          <span className="mt-1 block">
                            {entry.items!.map((it, i) => (
                              <span
                                key={`${entry.id}-${i}`}
                                className="block break-words text-[11px] text-td-muted"
                              >
                                · {it}
                              </span>
                            ))}
                          </span>
                        )}
                      </span>

                      <time
                        dateTime={entry.ts}
                        className="shrink-0 whitespace-nowrap text-[11px] text-td-muted"
                        title={new Date(entry.ts).toLocaleString("nl-BE")}
                      >
                        {formatRelativeTime(entry.ts)}
                      </time>
                    </li>
                  );
                })}
              </ul>

              {(canShowMore || canShowLess) && (
                <div className="mt-2 flex items-center justify-center gap-3">
                  {canShowMore && (
                    <button
                      type="button"
                      onClick={() => setVisibleCount((c) => c + PAGE_STEP)}
                      className="text-xs font-semibold text-td-blue hover:underline"
                    >
                      + {Math.min(PAGE_STEP, remaining)} meer ▾
                    </button>
                  )}
                  {canShowLess && (
                    <button
                      type="button"
                      onClick={() => setVisibleCount(INITIAL_VISIBLE)}
                      className="text-xs font-semibold text-td-blue hover:underline"
                    >
                      minder ▴
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {hasActivity && (
            <div
              className={
                hasChatter ? "mt-4 border-t border-gray-100 pt-3" : "mt-3"
              }
            >
              <h4 className="text-xs font-bold uppercase tracking-wide text-td-muted">
                Portaal-activiteit
              </h4>
              <ul className="mt-2 space-y-2">
                {activity.map((entry, i) => (
                  <li
                    key={`${entry.ts}-${i}`}
                    className="flex items-start justify-between gap-3 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="font-semibold break-words text-td-navy">
                        {entry.action}
                      </span>
                      {entry.detail && (
                        <span className="break-words text-td-muted">
                          {" "}
                          — {entry.detail}
                        </span>
                      )}
                      {entry.userName && (
                        <span className="block text-xs text-td-muted">
                          {entry.userName}
                        </span>
                      )}
                    </span>
                    <time
                      dateTime={entry.ts}
                      className="shrink-0 whitespace-nowrap text-xs text-td-muted"
                      title={new Date(entry.ts).toLocaleString("nl-BE")}
                    >
                      {formatRelativeTime(entry.ts)}
                    </time>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Wrapper>
  );
}
